import * as React from 'react';
import { scrollToAnchor, throttle } from '@cian/newbuilding-utils';
import { ArticleHeading2 } from '@cian/ui-kit';
import { Tabs } from '@cian/ui-kit/prev';
import { clsx } from 'clsx';
import { HEADER_OFFSET_SCROLL, SECTION_PADDING_TOP, STICKY_HEADER_HEIGHT } from '../../constants';
import { getCurrentScrollOffset } from '../../utils/dom/getCurrentScrollOffset';
import { Layout } from '../Layout';
import {
  collectClauses,
  getActiveTab,
  mapClausesToTabs,
  prepareTabs,
  getCurrentDocHeight,
  trackStickyHeaderTabClick,
} from './helpers';
import { ITabData } from './types';
import * as styles from './Header.css';

export interface IHeaderProps {
  name: string;
  isFixed: boolean;
  call: React.ReactElement<any>;
  callback: React.ReactElement<any>;
  logoSrc?: string;
}

export const Header = ({ name, isFixed, call, callback, logoSrc }: IHeaderProps) => {
  const [activeTab, setActiveTab] = React.useState<string>('main');
  const [tabs, setTabs] = React.useState<ITabData[]>([]);
  const [documentHeight, setDocumentHeight] = React.useState<number>(0);
  const preparedTabs = React.useMemo(() => prepareTabs({ tabs }), [tabs]);

  /* istanbul ignore next */
  const updateActiveTab = React.useCallback(() => {
    const yOffset = getCurrentScrollOffset();
    const activeTab = getActiveTab(yOffset + SECTION_PADDING_TOP, tabs);

    if (activeTab) {
      setActiveTab(activeTab.anchor);
    }
  }, [tabs]);

  const updateTabData = React.useCallback(() => {
    const clauses = collectClauses();
    const tabs = mapClausesToTabs(clauses);

    setTabs(tabs);
  }, []);

  /* istanbul ignore next */
  const checkDocHeight = React.useCallback((): boolean => {
    const currentHeight = getCurrentDocHeight();
    if (documentHeight !== currentHeight) {
      setDocumentHeight(currentHeight);

      return true;
    }

    return false;
  }, [documentHeight]);

  const onNavigationLinkClick = React.useCallback((slug: string) => {
    trackStickyHeaderTabClick(slug);

    const anchor = `#${slug}`;
    const event = new Event('click');

    if (slug === 'main') {
      scrollToAnchor({ anchor: '#header-frontend', hasRelocation: false, offset: 0 })(event);
    } else {
      // Для ff и safari нужен запас в 10 для корректного определения активного таба
      scrollToAnchor({ anchor, hasRelocation: false, offset: HEADER_OFFSET_SCROLL })(event);
    }
    setTimeout(() => {
      setActiveTab(slug);
    }, 605);
  }, []);

  /* istanbul ignore next */
  const throttledScrollHandler = throttle(600, () => {
    if (checkDocHeight()) {
      updateTabData();
    }
    updateActiveTab();
  });

  /* istanbul ignore next */
  const throttledResizeHandler = throttle(400, () => {
    updateTabData();
  });

  React.useEffect(() => {
    updateTabData();
  }, [updateTabData]);

  React.useEffect(() => {
    const anchor = window.location.hash;
    const slug = anchor.slice(1);

    /* istanbul ignore next */ if (slug) {
      setActiveTab(slug);
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener('scroll', throttledScrollHandler);
    window.addEventListener('resize', throttledResizeHandler);

    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
      window.removeEventListener('resize', throttledResizeHandler);
    };
  }, [documentHeight, throttledResizeHandler, throttledScrollHandler]);

  return (
    <div
      style={{ height: `${STICKY_HEADER_HEIGHT}px` }}
      className={clsx(styles['header'], isFixed && styles['header--fixed'])}
    >
      <Layout stretch={true}>
        <div className={styles['content']}>
          <div className={styles['title']}>
            {logoSrc ? (
              <img alt={name} className={styles['logo']} src={logoSrc} />
            ) : (
              <ArticleHeading2 whiteSpace={'nowrap'} as="div">
                {name}
              </ArticleHeading2>
            )}
          </div>
          <div className={styles['controls']}>
            {call}
            {callback}
          </div>
        </div>
        <div className={styles['nav']}>
          <nav className={styles['tabs']}>
            <Tabs activeTab={activeTab} onChange={onNavigationLinkClick} tabs={preparedTabs} />
          </nav>
        </div>
      </Layout>
    </div>
  );
};
